import { DataProvider, fetchUtils } from 'react-admin';
import { stringify } from 'query-string';
import { tenantService } from '../services/tenantService';

const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

// CRM resources
export const crmResources = [
  'customers',
  'customer-addresses',
  'contact-logs',
  'opportunities',
  'boards',
  'bills',
  'invoices',
  'leads',
  'estimates',
  'crm/service-blueprints',
  'crm/code-tables/lot-sizes',
  'crm/customer-types',
  'crm/zones',
  'crm/locations',
];

// Helper function to build query parameters for CRM resources
const buildCrmQueryParams = (params: any) => {
  const { page, perPage } = params.pagination || { page: 1, perPage: 25 };
  const { field, order } = params.sort || { field: 'id', order: 'DESC' };

  return {
    page: page,
    perPage: perPage,
    orderByField: field,
    order: order.toLowerCase(),
    ...params.filter,
  };
};

// HTTP client with authentication and tenant headers
const httpClient = async (url: string, options: any = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }

  // Only set Content-Type if not using FormData
  if (
    !(options.body instanceof FormData) &&
    options.method !== 'GET' &&
    options.method !== 'DELETE'
  ) {
    options.headers.set('Content-Type', 'application/json');
  }

  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    options.headers.set('Authorization', `Bearer ${accessToken}`);
  }

  // Add tenant code header (similar to fortress-web approach)
  try {
    const tenantCode = tenantService.getCurrentTenantCode();
    options.headers.set('x-tenant-code', tenantCode);
  } catch (error) {
    throw new Error('No tenant selected. Please select a tenant to continue.');
  }

  try {
    const response = await fetchUtils.fetchJson(url, options);
    // fetchJson returns { json, status, headers }
    // Handle 201 Created responses the same as 200 OK
    if (response.status === 201 || response.status === 200) {
      // Ensure json is properly parsed - fetchJson should handle this, but double-check
      if (!response.json && response.status === 201) {
        // If json is missing for 201, try to parse from response body
        console.warn('Response json is missing for 201 status, response:', response);
      }
      return response;
    }
    // For other success status codes, return as-is
    if (response.status >= 200 && response.status < 300) {
      return response;
    }
    // If we get here, it's an error status
    throw new Error(`HTTP ${response.status}: ${JSON.stringify(response.json)}`);
  } catch (error: any) {
    if (error.status === 401) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    throw error;
  }
};

// Helper function to map BillState to frontend status
const mapBillStateToStatus = (state: string): string => {
  switch (state) {
    case 'Estimate':
      return 'draft';
    case 'Approved':
      return 'approved';
    case 'DeadEstimate':
      return 'rejected';
    case 'Closed':
      return 'approved';
    default:
      return state?.toLowerCase() || 'draft';
  }
};

// Helper function to transform bill to estimate format
const transformBillToEstimate = (item: any) => {
  // Transform customerServices to lineItems if they exist
  let lineItems: any[] = [];
  if (item.customerServices && Array.isArray(item.customerServices) && item.customerServices.length > 0) {
    lineItems = item.customerServices.map((cs: any) => {
      const serviceBlueprint = cs.serviceBlueprint || {};
      // CustomerService doesn't have quantity or price fields directly
      // They come from the serviceBlueprint relation
      const name = serviceBlueprint.name || cs.serviceName || 'Service';
      const description = cs.notes || serviceBlueprint.description || '';
      const unitPrice = serviceBlueprint.basePrice || cs.servicePrice || 0;
      const quantity = cs.quantity || 1; // CustomerService represents a single service instance by default
      
      return {
        id: cs.id,
        serviceBlueprintId: cs.serviceBlueprintId || serviceBlueprint.id,
        serviceBlueprint: serviceBlueprint,
        name: name,
        description: description,
        quantity: quantity,
        unitPrice: unitPrice,
        taxRate: 0, // Tax is calculated globally
        amount: unitPrice * quantity,
        estimatedDuration: cs.duration || serviceBlueprint.estimatedDuration,
        isCustom: !cs.serviceBlueprintId, // Custom if no blueprint linked
      };
    });
  } else if (item.lineItems && Array.isArray(item.lineItems)) {
    // If lineItems already exist, use them
    lineItems = item.lineItems;
  }

  // Convert date strings to Date objects if needed
  const parseDate = (dateValue: any): Date => {
    if (!dateValue) return new Date();
    if (dateValue instanceof Date) return dateValue;
    if (typeof dateValue === 'string') return new Date(dateValue);
    return new Date();
  };

  return {
    ...item,
    estimateNumber: item.billNumber,
    status: mapBillStateToStatus(item.state),
    // Keep state for backward compatibility
    state: item.state,
    lineItems: lineItems,
    // Map billDate to issueDate if needed, and convert to Date
    issueDate: parseDate(item.issueDate || item.billDate),
    // Map validUntil if it exists (might need to calculate from billDate + 30 days)
    validUntil: item.validUntil ? parseDate(item.validUntil) : parseDate(item.billDate),
    // Map notes to terms if terms doesn't exist
    terms: item.terms || item.notes || 'Payment due within 30 days of estimate approval.',
  };
};

export const crmDataProvider: DataProvider = {
  getList: async (resource, params) => {
    // Special handling for estimates - use bills API
    if (resource === 'estimates') {
      const query = buildCrmQueryParams(params);
      const url = `${baseUrl}/crm/bills?${stringify(query)}`;

      try {
        const { json, headers } = await httpClient(url);

        let data, total;

        if (json.data && json.metadata) {
          data = json.data;
          total = json.metadata.total;
        } else if (Array.isArray(json)) {
          data = json;
          total = parseInt(headers.get('X-Total-Count') || json.length.toString(), 10);
        } else {
          data = json.data || json;
          total = json.total || parseInt(headers.get('X-Total-Count') || '0', 10);
        }

        // Transform billNumber to estimateNumber and state to status for frontend
        if (Array.isArray(data)) {
          data = data.map(transformBillToEstimate);
        }

        return { data, total };
      } catch (error) {
        console.error(`Error fetching estimates:`, error);
        throw error;
      }
    }

    // Special handling for contact-logs - unified API
    if (resource === 'contact-logs') {
      const query = buildCrmQueryParams(params);
      const url = `${baseUrl}/crm/contact-logs?${stringify(query)}`;

      try {
        const { json, headers } = await httpClient(url);

        let data, total;

        if (json.data && json.metadata) {
          data = json.data;
          total = json.metadata.total;
        } else if (Array.isArray(json)) {
          data = json;
          total = parseInt(headers.get('X-Total-Count') || json.length.toString(), 10);
        } else {
          data = json.data || json;
          total = json.total || parseInt(headers.get('X-Total-Count') || '0', 10);
        }

        return { data, total };
      } catch (error) {
        console.error(`Error fetching contact logs:`, error);
        throw error;
      }
    }

    // Special handling for customer addresses - they use nested routes
    if (resource === 'customer-addresses') {
      const { customerId, ...otherFilters } = params.filter || {};
      if (!customerId) {
        throw new Error('customerId is required for customer-addresses resource');
      }

      const url = `${baseUrl}/crm/customers/${customerId}/addresses`;

      try {
        const { json } = await httpClient(url);
        const data = Array.isArray(json) ? json : [json];

        // Apply additional filters if needed
        let filteredData = data;
        if (Object.keys(otherFilters).length > 0) {
          filteredData = data.filter((item: any) => {
            return Object.entries(otherFilters).every(([key, value]) => {
              return item[key] === value;
            });
          });
        }

        // Apply sorting if specified
        if (params.sort && params.sort.field) {
          filteredData.sort((a: any, b: any) => {
            const aVal = a[params.sort.field];
            const bVal = b[params.sort.field];
            if (params.sort.order === 'ASC') {
              return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
            } else {
              return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
            }
          });
        }

        return { data: filteredData, total: filteredData.length };
      } catch (error) {
        console.error(`Error fetching customer addresses for customer ${customerId}:`, error);
        throw error;
      }
    }

    const query = buildCrmQueryParams(params);
    // Handle resources that already have crm/ prefix
    const resourcePath = resource.startsWith('crm/') ? resource : `crm/${resource}`;
    const url = `${baseUrl}/${resourcePath}?${stringify(query)}`;

    try {
      const { json, headers } = await httpClient(url);

      let data, total;

      if (json.data && json.metadata) {
        data = json.data;
        total = json.metadata.total;
      } else if (Array.isArray(json)) {
        data = json;
        total = parseInt(headers.get('X-Total-Count') || json.length.toString(), 10);
      } else {
        data = json.data || json;
        total = json.total || parseInt(headers.get('X-Total-Count') || '0', 10);
      }

      return { data, total };
    } catch (error) {
      console.error(`Error fetching CRM ${resource} list:`, error);
      throw error;
    }
  },

  getOne: async (resource, params) => {
    // Special handling for estimates - use bills API
    if (resource === 'estimates') {
      const url = `${baseUrl}/crm/bills/${params.id}`;

      try {
        const { json } = await httpClient(url);
        // Transform bill to estimate format with status mapping
        const data = transformBillToEstimate(json);
        return { data };
      } catch (error) {
        console.error(`Error fetching estimate ${params.id}:`, error);
        throw error;
      }
    }

    const resourcePath = resource.startsWith('crm/') ? resource : `crm/${resource}`;
    const url = `${baseUrl}/${resourcePath}/${params.id}`;

    try {
      const { json } = await httpClient(url);
      return { data: json };
    } catch (error) {
      console.error(`Error fetching CRM ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  getMany: async (resource, params) => {
    try {
      // Special handling for estimates - use bills API
      if (resource === 'estimates') {
        const promises = params.ids.map(id =>
          httpClient(`${baseUrl}/crm/bills/${id}`)
            .then(({ json }) => transformBillToEstimate(json))
            .catch(() => null)
        );

        const results = await Promise.all(promises);
        const data = results.filter(item => item !== null);
        return { data };
      }

      const resourcePath = resource.startsWith('crm/') ? resource : `crm/${resource}`;
      const promises = params.ids.map(id =>
        httpClient(`${baseUrl}/${resourcePath}/${id}`)
          .then(({ json }) => json)
          .catch(() => null)
      );

      const results = await Promise.all(promises);
      const data = results.filter(item => item !== null);
      return { data };
    } catch (error) {
      console.error(`Error fetching multiple CRM ${resource}:`, error);
      return { data: [] };
    }
  },

  getManyReference: async (resource, params) => {
    const query = buildCrmQueryParams(params);
    query[params.target] = params.id;

    // Special handling for estimates - use bills API
    if (resource === 'estimates') {
      const url = `${baseUrl}/crm/bills?${stringify(query)}`;

      try {
        const { json, headers } = await httpClient(url);

        let data, total;

        if (json.data && json.metadata) {
          data = json.data;
          total = json.metadata.total;
        } else if (Array.isArray(json)) {
          data = json;
          total = parseInt(headers.get('X-Total-Count') || json.length.toString(), 10);
        } else {
          data = json.data || json;
          total = json.total || parseInt(headers.get('X-Total-Count') || '0', 10);
        }

        return { data, total };
      } catch (error) {
        console.error(`Error fetching estimate references:`, error);
        return { data: [], total: 0 };
      }
    }

    const resourcePath = resource.startsWith('crm/') ? resource : `crm/${resource}`;
    const url = `${baseUrl}/${resourcePath}?${stringify(query)}`;

    try {
      const { json, headers } = await httpClient(url);

      let data, total;

      if (json.data && json.metadata) {
        data = json.data;
        total = json.metadata.total;
      } else if (Array.isArray(json)) {
        data = json;
        total = parseInt(headers.get('X-Total-Count') || json.length.toString(), 10);
      } else {
        data = json.data || json;
        total = json.total || parseInt(headers.get('X-Total-Count') || '0', 10);
      }

      return { data, total };
    } catch (error) {
      console.error(`Error fetching CRM ${resource} references:`, error);
      return { data: [], total: 0 };
    }
  },

  create: async (resource, params) => {
    // Special handling for estimates - use bills API
    if (resource === 'estimates') {
      const url = `${baseUrl}/crm/bills`;

      // Transform estimateNumber to billNumber for API and serialize dates
      const dataToSend: any = {
        ...params.data,
      };

      // Only include billNumber if estimateNumber is manually provided (non-empty)
      // If empty, let the server auto-generate it
      if (params.data.estimateNumber && params.data.estimateNumber.trim()) {
        dataToSend.billNumber = params.data.estimateNumber.trim();
      }
      // If billNumber is already set, use it
      if (params.data.billNumber && params.data.billNumber.trim()) {
        dataToSend.billNumber = params.data.billNumber.trim();
      }

      // Convert Date objects to ISO strings and map issueDate to billDate
      if (dataToSend.issueDate instanceof Date) {
        dataToSend.billDate = dataToSend.issueDate.toISOString();
        delete dataToSend.issueDate;
      } else if (dataToSend.issueDate) {
        dataToSend.billDate = dataToSend.issueDate;
        delete dataToSend.issueDate;
      }
      if (dataToSend.validUntil instanceof Date) {
        // validUntil is not a field in Bill model, so we'll store it in notes or remove it
        delete dataToSend.validUntil;
      }

      // Remove estimateNumber from the data sent to API (since we're using billNumber)
      delete dataToSend.estimateNumber;
      
      // Always include lineItems (empty array if none) so backend knows there are no services
      dataToSend.lineItems = Array.isArray(params.data.lineItems) ? params.data.lineItems : [];

      try {
        console.log('Creating estimate with data:', dataToSend);
        const response = await httpClient(url, {
          method: 'POST',
          body: JSON.stringify(dataToSend),
        });

        console.log('Estimate creation response:', response);
        console.log('Response type:', typeof response);
        console.log('Response keys:', Object.keys(response || {}));
        console.log('Response json:', response.json);
        console.log('Response status:', response.status);

        // fetchJson returns { json, status, headers }
        // Handle both the wrapped response structure and direct JSON
        let json;
        if (response && typeof response === 'object') {
          // If response has a json property, use it (standard fetchJson response)
          if ('json' in response && response.json !== undefined) {
            json = response.json;
            console.log('Using response.json:', json);
          } 
          // If response itself looks like the data (has id, billNumber, etc.), use it directly
          else if ('id' in response || 'billNumber' in response) {
            json = response;
            console.log('Using response directly as data:', json);
          }
          // Otherwise, try to use response as-is
          else {
            json = response;
            console.log('Using response as-is:', json);
          }
        } else {
          json = response;
          console.log('Response is not an object, using as-is:', json);
        }
        
        // More lenient check - allow empty objects but check for required fields
        if (!json) {
          console.error('No JSON response at all:', response);
          throw new Error('Server returned empty or invalid response');
        }
        
        // Check if json is an empty object or missing required fields
        if (typeof json === 'object' && Object.keys(json).length === 0) {
          console.error('Empty JSON object:', json);
          throw new Error('Server returned empty response object');
        }
        
        // Validate that we have at least an id or billNumber
        if (!json.id && !json.billNumber) {
          console.error('Response missing required fields (id or billNumber):', json);
          throw new Error('Server response missing required fields');
        }

        console.log('Estimate created successfully with json:', json);
        console.log('CustomerServices in response:', json.customerServices);

        // Transform the created bill to estimate format with status mapping
        const transformedData = transformBillToEstimate(json);

        console.log('Transformed data:', transformedData);
        console.log('LineItems in transformed data:', transformedData.lineItems);
        console.log('Transformed data id:', transformedData.id);
        console.log('Transformed data estimateNumber:', transformedData.estimateNumber);

        // Ensure we have an id
        if (!transformedData.id) {
          console.error('Transformed data missing id:', transformedData);
          throw new Error('Failed to transform estimate: Missing ID');
        }

        return {
          data: transformedData,
        };
      } catch (error: any) {
        console.error(`Error creating estimate:`, error);
        console.error('Error details:', {
          message: error.message,
          status: error.status,
          body: error.body,
        });
        
        // Extract error message from response body if available
        let errorMessage = error.message;
        if (error.body) {
          try {
            const body = typeof error.body === 'string' ? JSON.parse(error.body) : error.body;
            errorMessage = body.message || body.error || errorMessage;
          } catch (e) {
            // If parsing fails, use the original message
          }
        }
        
        // Create a new error with the extracted message
        const enhancedError = new Error(errorMessage);
        (enhancedError as any).status = error.status;
        (enhancedError as any).body = error.body;
        throw enhancedError;
      }
    }

    // Special handling for contact-logs - unified API
    if (resource === 'contact-logs') {
      const url = `${baseUrl}/crm/contact-logs`;

      try {
        const { json } = await httpClient(url, {
          method: 'POST',
          body: JSON.stringify(params.data),
        });

        return {
          data: { ...params.data, ...json },
        };
      } catch (error) {
        console.error(`Error creating contact log:`, error);
        throw error;
      }
    }

    // Special handling for customer addresses - they use nested routes
    if (resource === 'customer-addresses') {
      const { customerId, ...addressData } = params.data;
      if (!customerId) {
        throw new Error('customerId is required for creating customer addresses');
      }

      const url = `${baseUrl}/crm/customers/${customerId}/addresses`;

      try {
        const { json } = await httpClient(url, {
          method: 'POST',
          body: JSON.stringify(addressData),
        });

        return {
          data: { ...params.data, ...json },
        };
      } catch (error) {
        console.error(`Error creating customer address:`, error);
        throw error;
      }
    }

    const resourcePath = resource.startsWith('crm/') ? resource : `crm/${resource}`;
    const url = `${baseUrl}/${resourcePath}`;

    try {
      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(params.data),
      });

      return {
        data: { ...params.data, ...json },
      };
    } catch (error) {
      console.error(`Error creating CRM ${resource}:`, error);
      throw error;
    }
  },

  update: async (resource, params) => {
    // Special handling for estimates - use bills API
    if (resource === 'estimates') {
      const url = `${baseUrl}/crm/bills/${params.id}`;

      // Transform estimateNumber to billNumber for API and serialize dates
      const dataToSend: any = {
        ...params.data,
      };

      // Map estimateNumber to billNumber
      if (params.data.estimateNumber && params.data.estimateNumber.trim()) {
        dataToSend.billNumber = params.data.estimateNumber.trim();
      }
      if (params.data.billNumber && params.data.billNumber.trim()) {
        dataToSend.billNumber = params.data.billNumber.trim();
      }

      // Convert Date objects to ISO strings and map issueDate to billDate
      if (dataToSend.issueDate instanceof Date) {
        dataToSend.billDate = dataToSend.issueDate.toISOString();
        delete dataToSend.issueDate;
      } else if (dataToSend.issueDate) {
        dataToSend.billDate = dataToSend.issueDate;
        delete dataToSend.issueDate;
      }
      if (dataToSend.validUntil instanceof Date) {
        // validUntil is not a field in Bill model, so we'll remove it
        delete dataToSend.validUntil;
      }

      // Remove estimateNumber from the data sent to API (since we're using billNumber)
      delete dataToSend.estimateNumber;
      
      // Always include lineItems (empty array if none) so backend knows there are no services
      dataToSend.lineItems = Array.isArray(params.data.lineItems) ? params.data.lineItems : [];

      try {
        const { json } = await httpClient(url, {
          method: 'PATCH',
          body: JSON.stringify(dataToSend),
        });

        // Transform the updated bill to estimate format
        const transformedData = transformBillToEstimate(json);

        return {
          data: transformedData,
        };
      } catch (error) {
        console.error(`Error updating estimate ${params.id}:`, error);
        throw error;
      }
    }

    // Special handling for contact-logs - unified API
    if (resource === 'contact-logs') {
      const url = `${baseUrl}/crm/contact-logs/${params.id}`;

      try {
        const { json } = await httpClient(url, {
          method: 'PATCH',
          body: JSON.stringify(params.data),
        });

        return { data: json };
      } catch (error) {
        console.error(`Error updating contact log ${params.id}:`, error);
        throw error;
      }
    }

    // Special handling for customer addresses - they use nested routes
    if (resource === 'customer-addresses') {
      const { customerId, ...addressData } = params.data;
      if (!customerId) {
        throw new Error('customerId is required for updating customer addresses');
      }

      const url = `${baseUrl}/crm/customers/${customerId}/addresses/${params.id}`;

      try {
        const { json } = await httpClient(url, {
          method: 'PATCH',
          body: JSON.stringify(addressData),
        });

        return { data: json };
      } catch (error) {
        console.error(`Error updating customer address ${params.id}:`, error);
        throw error;
      }
    }

    const resourcePath = resource.startsWith('crm/') ? resource : `crm/${resource}`;
    const url = `${baseUrl}/${resourcePath}/${params.id}`;

    try {
      const { json } = await httpClient(url, {
        method: 'PATCH',
        body: JSON.stringify(params.data),
      });

      return { data: json };
    } catch (error) {
      console.error(`Error updating CRM ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  updateMany: async (resource, params) => {
    try {
      // Special handling for estimates - use bills API
      if (resource === 'estimates') {
        await Promise.all(
          params.ids.map(id =>
            httpClient(`${baseUrl}/crm/bills/${id}`, {
              method: 'PATCH',
              body: JSON.stringify(params.data),
            })
          )
        );

        return { data: params.ids };
      }

      const resourcePath = resource.startsWith('crm/') ? resource : `crm/${resource}`;
      await Promise.all(
        params.ids.map(id =>
          httpClient(`${baseUrl}/${resourcePath}/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(params.data),
          })
        )
      );

      return { data: params.ids };
    } catch (error) {
      console.error(`Error updating multiple CRM ${resource}:`, error);
      throw error;
    }
  },

  delete: async (resource, params) => {
    // Special handling for estimates - use bills API
    if (resource === 'estimates') {
      const url = `${baseUrl}/crm/bills/${params.id}`;

      try {
        const { json } = await httpClient(url, {
          method: 'DELETE',
        });

        return { data: json };
      } catch (error) {
        console.error(`Error deleting estimate ${params.id}:`, error);
        throw error;
      }
    }

    // Special handling for contact-logs - unified API
    if (resource === 'contact-logs') {
      const url = `${baseUrl}/crm/contact-logs/${params.id}`;

      try {
        const { json } = await httpClient(url, {
          method: 'DELETE',
        });

        return { data: json };
      } catch (error) {
        console.error(`Error deleting contact log ${params.id}:`, error);
        throw error;
      }
    }

    // Special handling for customer addresses - they use nested routes
    if (resource === 'customer-addresses') {
      const customerId = params.meta?.customerId;
      if (!customerId) {
        throw new Error('customerId is required for deleting customer addresses');
      }

      const url = `${baseUrl}/crm/customers/${customerId}/addresses/${params.id}`;

      try {
        const { json } = await httpClient(url, {
          method: 'DELETE',
        });

        return { data: json };
      } catch (error) {
        console.error(`Error deleting customer address ${params.id}:`, error);
        throw error;
      }
    }

    const resourcePath = resource.startsWith('crm/') ? resource : `crm/${resource}`;
    const url = `${baseUrl}/${resourcePath}/${params.id}`;

    try {
      const { json } = await httpClient(url, {
        method: 'DELETE',
      });

      return { data: json };
    } catch (error) {
      console.error(`Error deleting CRM ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  deleteMany: async (resource, params) => {
    try {
      const resourcePath = resource.startsWith('crm/') ? resource : `crm/${resource}`;
      await Promise.all(
        params.ids.map(id =>
          httpClient(`${baseUrl}/${resourcePath}/${id}`, {
            method: 'DELETE',
          })
        )
      );

      return { data: params.ids };
    } catch (error) {
      console.error(`Error deleting multiple CRM ${resource}:`, error);
      throw error;
    }
  },
};
